import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Globe, Menu, X, ChevronDown, Shield, Clock, Headphones } from 'lucide-react';
import { Button } from './ui/button';
import { SERVICES, INDUSTRIES, getRelatedServices } from '../data/seo-data';

const PHONE_NUMBERS = {
  US: { label: '1 (855) 340-8855', tel: '+18553408855', region: 'Toll Free' },
  CA: { label: '1 (647) 493-0800', tel: '+16474930800', region: 'Canada' },
  AU: { label: '(03) 8899 7474', tel: '+61388997474', region: 'Australia' },
  GB: { label: '(07) 40393 6600', tel: '+4740393600', region: 'England' },
  DEFAULT: { label: '1 (855) 340-8855', tel: '+18553408855', region: 'Toll Free' },
};

function useGeoPhone() {
  const [phone, setPhone] = useState(PHONE_NUMBERS.DEFAULT);
  useEffect(() => {
    const cached = sessionStorage.getItem('geo_phone');
    if (cached) { try { setPhone(JSON.parse(cached)); return; } catch {} }
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(data => {
        const code = data.country_code || '';
        let match = PHONE_NUMBERS[code] || PHONE_NUMBERS.DEFAULT;
        if (code === 'US' && data.city === 'Chicago') {
          match = { label: '1 (312) 313-6001', tel: '+13123136001', region: 'Chicago' };
        }
        setPhone(match);
        sessionStorage.setItem('geo_phone', JSON.stringify(match));
      })
      .catch(() => {});
  }, []);
  return phone;
}

const NAV = [
  { label: 'Services', children: [
    { label: 'Phone Interpreting (OPI)', path: '/services/phone-interpreting' },
    { label: 'Video Interpreting (VRI)', path: '/services/video-interpreting' },
    { label: 'On-Site Interpreting', path: '/services/on-site-interpreting' },
    { label: 'AI Interpreting', path: '/services/ai-interpreting' },
  ]},
  { label: 'Industries', children: [
    { label: 'Healthcare', path: '/industries/healthcare-interpreting-services' },
    { label: 'Legal & Court', path: '/industries/legal-interpreting-services' },
    { label: 'Government', path: '/industries/government-interpreting-services' },
    { label: 'Policing & Law Enforcement', path: '/industries/policing-interpreting-services' },
    { label: 'Immigration', path: '/industries/immigration-interpreting-services' },
    { label: 'Corrections & Prisons', path: '/industries/corrections-interpreting-services' },
    { label: 'Call Centers', path: '/industries/call-centers-interpreting-services' },
    { label: 'Corporate', path: '/industries/corporate-interpreting-services' },
    { label: 'Schools & Education', path: '/industries/education-interpreting-services' },
  ]},
  { label: 'Pricing', path: '/pricing' },
  { label: 'Languages', children: [
    { label: 'Spanish Interpreter', path: '/spanish-interpreter-services' },
    { label: 'Mandarin Interpreter', path: '/mandarin-interpreter-services' },
    { label: 'Arabic Interpreter', path: '/arabic-interpreter-services' },
    { label: 'French Interpreter', path: '/french-interpreter-services' },
    { label: 'Vietnamese Interpreter', path: '/vietnamese-interpreter-services' },
    { label: 'Korean Interpreter', path: '/korean-interpreter-services' },
    { label: 'Russian Interpreter', path: '/russian-interpreter-services' },
    { label: 'Portuguese Interpreter', path: '/portuguese-interpreter-services' },
    { label: 'Tagalog Interpreter', path: '/tagalog-interpreter-services' },
    { label: 'Hindi Interpreter', path: '/hindi-interpreter-services' },
    { label: 'All 250+ Languages', path: '/languages' },
  ]},
  { label: 'Locations', path: '/locations' },
  { label: 'How It Works', children: [
    { label: 'How It Works', path: '/how-it-works' },
    { label: 'Security & Privacy', path: '/security' },
    { label: 'Blog', path: '/blog' },
  ]},
  { label: 'Contact', path: '/contact' },
];

export function SEOHead({ title, description, canonical, schema }) {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', description);
    setMeta('og:title', title);
    setMeta('og:description', description);
    setMeta('og:type', 'website');
    if (canonical) setMeta('og:url', canonical);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!canonicalEl) { canonicalEl = document.createElement('link'); canonicalEl.setAttribute('rel', 'canonical'); document.head.appendChild(canonicalEl); }
      canonicalEl.setAttribute('href', canonical);
    } else if (canonicalEl) { canonicalEl.remove(); }

    let schemaEl = document.querySelector('script[data-seo-schema]');
    if (schema) {
      if (!schemaEl) { schemaEl = document.createElement('script'); schemaEl.setAttribute('type', 'application/ld+json'); schemaEl.setAttribute('data-seo-schema', 'true'); document.head.appendChild(schemaEl); }
      schemaEl.textContent = JSON.stringify(schema);
    } else if (schemaEl) { schemaEl.remove(); }

    return () => {
      document.title = 'Interpreter Services | Professional Interpreters in 250+ Languages';
      if (canonicalEl && canonical) canonicalEl.remove();
      if (schemaEl && schema) schemaEl.remove();
    };
  }, [title, description, canonical, schema]);
  return null;
}

export function TrustBadges() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-8 py-6 bg-slate-50 border-b">
      <div className="flex items-center gap-2 text-sm text-slate-600"><Clock className="w-4 h-4 text-blue-600" /><span>Smart Interpreting, Your Way</span></div>
      <div className="flex items-center gap-2 text-sm text-slate-600"><Headphones className="w-4 h-4 text-blue-600" /><span>Human Escalation On Demand</span></div>
      <div className="flex items-center gap-2 text-sm text-slate-600"><Shield className="w-4 h-4 text-blue-600" /><span>Enterprise Ready</span></div>
      <div className="flex items-center gap-2 text-sm text-slate-600"><Globe className="w-4 h-4 text-blue-600" /><span>250+ Languages</span></div>
    </div>
  );
}

export function CTASection({ variant = 'primary' }) {
  const bg = variant === 'primary' ? 'bg-blue-700' : 'bg-slate-900';
  return (
    <section className={`${bg} text-white py-16`} data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl font-bold mb-4">Language Access Without Delay</h2>
        <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
          AI when you want it. Human when you need it. Start free or talk to our team about enterprise solutions.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <a href="/auth?tab=register" target="_blank" rel="noopener noreferrer"><Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8" data-testid="cta-start-free" style={{display:'none'}}>Start Free</Button></a>
          <Link to="/book-demo"><Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-8" data-testid="cta-book-demo">Book Demo</Button></Link>
          <Link to="/book-interpreter"><Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-8">Book Interpreter</Button></Link>
        </div>
      </div>
    </section>
  );
}

export function InternalLinks({ cities = [], languages = [], services = [] }) {
  if (!cities.length && !languages.length && !services.length) return null;
  return (
    <section className="bg-slate-50 py-12">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-xl font-bold text-slate-900 mb-6">Explore Our Interpreter Services</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {cities.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Nearby Locations</h3>
              <ul className="space-y-2">{cities.map(c => {
                const slug = c.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                return <li key={slug}><Link to={`/interpreting-services-${slug}`} className="text-blue-700 hover:underline text-sm">{c} Interpreter Services</Link></li>;
              })}</ul>
            </div>
          )}
          {languages.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Language Services</h3>
              <ul className="space-y-2">{languages.map(l => (
                <li key={l.slug}><Link to={`/${l.slug}-interpreter-services`} className="text-blue-700 hover:underline text-sm">{l.name} Interpreter Services</Link></li>
              ))}</ul>
            </div>
          )}
          {services.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Our Services</h3>
              <ul className="space-y-2">{services.map(s => (
                <li key={s.slug}><Link to={`/services/${s.slug}`} className="text-blue-700 hover:underline text-sm">{s.name}</Link></li>
              ))}</ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function NavDropdown({ item, mobile, onNavigate }) {
  const [open, setOpen] = useState(false);
  const handleClick = () => { setOpen(false); if (onNavigate) onNavigate(); };
  if (mobile) {
    return (
      <div>
        <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full py-2 text-slate-700 font-medium">
          {item.label}<ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && <div className="pl-4 space-y-1">{item.children.map(c => (
          <Link key={c.path} to={c.path} className="block py-1.5 text-sm text-slate-600 hover:text-blue-700" onClick={handleClick}>{c.label}</Link>
        ))}</div>}
      </div>
    );
  }
  return (
    <div className="relative group" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-blue-700 py-2">
        {item.label}<ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute top-full left-0 bg-white rounded-lg shadow-lg border py-2 min-w-[220px] z-50">
          {item.children.map(c => (
            <Link key={c.path} to={c.path} className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700" onClick={handleClick}>{c.label}</Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PublicLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const geoPhone = useGeoPhone();

  useEffect(() => {
    window.scrollTo(0, 0);
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Sticky Header Wrapper */}
      <div className="sticky top-0 z-40">
      {/* Top Bar */}
      <div className="bg-blue-700 text-white text-xs py-1.5">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> <a href={`tel:${geoPhone.tel}`}>{geoPhone.label}</a></span>
            <span className="hidden sm:inline">{geoPhone.region} &middot; 24/7 Interpreter Services</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="https://services.interpreter.io/onboarding/" target="_blank" rel="noopener noreferrer" className="hover:underline font-medium">Interpreter Login/Application</a>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="public-logo">
            <img src="/logo-coloured.png" alt="Interpreter IO" className="h-8" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map(item => item.children ? (
              <NavDropdown key={item.label} item={item} />
            ) : (
              <Link key={item.path} to={item.path} className={`text-sm font-medium py-2 ${location.pathname === item.path ? 'text-blue-700' : 'text-slate-700 hover:text-blue-700'}`}>{item.label}</Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href="/auth?tab=register" target="_blank" rel="noopener noreferrer"><Button className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="public-get-started-btn" style={{display:'none'}}>Get Started</Button></a>
          </div>

          {/* Mobile Toggle */}
          <button className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t bg-white px-6 py-4 space-y-2">
            {NAV.map(item => item.children ? (
              <NavDropdown key={item.label} item={item} mobile onNavigate={() => setMobileOpen(false)} />
            ) : (
              <Link key={item.path} to={item.path} className="block py-2 text-slate-700 font-medium" onClick={() => setMobileOpen(false)}>{item.label}</Link>
            ))}
            <div className="flex gap-3 pt-3 border-t">
              <a href="/auth?tab=register" target="_blank" rel="noopener noreferrer" className="flex-1"><Button className="w-full bg-blue-700" style={{display:'none'}}>Get Started</Button></a>
            </div>
          </div>
        )}
      </header>
      </div>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo-white.png" alt="Interpreter IO" className="h-8" />
              </div>
              <p className="text-sm text-slate-400 mb-4">The right interpreter, every time — AI or human interpreters. Real-time language access for enterprise environments. 250+ languages.</p>
              <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-blue-400" /><a href={`tel:${geoPhone.tel}`} className="hover:text-white">{geoPhone.label}</a></div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/security" className="hover:text-white">Security & Privacy</Link></li>
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/services/phone-interpreting" className="hover:text-white">Phone Interpreting</Link></li>
                <li><Link to="/services/video-interpreting" className="hover:text-white">Video Interpreting</Link></li>
                <li><Link to="/services/ai-interpreting" className="hover:text-white">AI Interpreting</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Industries</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/industries/healthcare-interpreting-services" className="hover:text-white">Healthcare</Link></li>
                <li><Link to="/industries/legal-interpreting-services" className="hover:text-white">Legal & Court</Link></li>
                <li><Link to="/industries/government-interpreting-services" className="hover:text-white">Government</Link></li>
                <li><Link to="/industries/policing-interpreting-services" className="hover:text-white">Policing</Link></li>
                <li><Link to="/industries/immigration-interpreting-services" className="hover:text-white">Immigration</Link></li>
                <li><Link to="/industries/corrections-interpreting-services" className="hover:text-white">Corrections</Link></li>
                <li><Link to="/industries/call-centers-interpreting-services" className="hover:text-white">Call Centers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/languages" className="hover:text-white">Languages</Link></li>
                <li><Link to="/locations" className="hover:text-white">Locations</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
                <li><a href="/auth" target="_blank" rel="noopener noreferrer" className="hover:text-white">Interpreter Login</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-700 pt-8 text-center text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} Interpreter Services. All rights reserved. Smart interpreting — AI or human interpreters — across the United States, Canada, the United Kingdom, and Australia.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
